// frontend/src/features/aw/AWApp.jsx
import React, { useState } from "react";
import AWDashboard from "./AWDashboard";
import AWImportWizard from "./AWImportWizard";
import AWClientManager from "./AWClientManager";
import AWAgentManager from "./AWAgentManager";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" },
  { key: "import", label: "Import", icon: "M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" },
  { key: "clients", label: "Clients", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" },
  { key: "agents", label: "Agents", icon: "M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0" },
];

export default function AWApp({ email, onClose, logout }) {
  const [tab, setTab] = useState("dashboard");
  const [refreshKey, setRefreshKey] = useState(0);

  const switchTab = (t) => {
    setTab(t);
    if (t === "dashboard") setRefreshKey(k => k + 1); // trigger dashboard reload
  };

  return (
    <div className="aw">
      <style>{CSS}</style>

      <header className="aw-hdr">
        <div className="aw-hdr-l">
          <button className="aw-back" onClick={onClose} title="Back to apps">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div className="aw-brand">
            <span className="aw-brand-badge">AW</span>
            <span className="aw-brand-name">Agent Watch</span>
          </div>
        </div>

        <nav className="aw-nav">
          {TABS.map(t => (
            <button key={t.key} className={`aw-tab ${tab === t.key ? "aw-tab-on" : ""}`}
              onClick={() => switchTab(t.key)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d={t.icon}/>
              </svg>
              {t.label}
            </button>
          ))}
        </nav>

        <div className="aw-hdr-r">
          <span className="aw-email">{email}</span>
          <button className="aw-logout" onClick={logout}>Sign out</button>
        </div>
      </header>

      <main className="aw-main">
        {/* Dashboard stays mounted (hidden) so scrape state survives tab switches */}
        <div style={{ display: tab === "dashboard" ? "block" : "none" }}>
          <AWDashboard onImport={() => switchTab("import")} refreshKey={refreshKey} />
        </div>
        {tab === "import" && <AWImportWizard onDone={() => switchTab("dashboard")} />}
        {tab === "clients" && <AWClientManager />}
        {tab === "agents" && <AWAgentManager />}
      </main>
    </div>
  );
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

/* ── Root ── */
.aw{
  --bg:#1b191e;--surface:rgba(40,36,42,.55);--surface2:#2e2a32;
  --border:#3d3841;--border-l:rgba(60,55,64,.45);
  --text:#ece8ed;--muted:#a0979a;--dim:#726a72;
  --accent:#8B2332;--accent-g:rgba(139,35,50,.12);
  --green:#2dd468;--red:#e8453a;--amber:#e8a020;--blue:#4a8ef5;--gray:#6b6577;
  --ease:cubic-bezier(0.16, 1, 0.3, 1);
  height:100vh;display:flex;flex-direction:column;overflow:hidden;
  font-family:'Plus Jakarta Sans',-apple-system,BlinkMacSystemFont,sans-serif;
  background:var(--bg);color:var(--text);
  font-variant-numeric:tabular-nums;
}

/* ── Header ── */
.aw-hdr{
  flex-shrink:0;display:flex;align-items:center;justify-content:space-between;
  padding:0 24px;height:56px;gap:16px;
  background:var(--bg);
  border-bottom:1px solid var(--border-l);z-index:20;
}
.aw-hdr-l{display:flex;align-items:center;gap:10px;}
.aw-hdr-r{display:flex;align-items:center;gap:10px;}
.aw-back{
  display:flex;align-items:center;justify-content:center;
  width:36px;height:36px;border-radius:8px;border:1px solid var(--border);
  background:transparent;cursor:pointer;color:var(--muted);
  transition:all .2s var(--ease);position:relative;
}
.aw-back::before{content:'';position:absolute;inset:-4px;}
.aw-back:hover{border-color:var(--accent);color:#cf4154;background:var(--accent-g);}
.aw-brand{display:flex;align-items:center;gap:8px;}
.aw-brand-badge{
  font-size:10px;font-weight:800;letter-spacing:1.2px;
  padding:3px 8px;border-radius:5px;
  background:var(--accent);color:#f5e8ea;
}
.aw-brand-name{font-size:15px;font-weight:700;color:var(--text);}

/* ── Nav ── */
.aw-nav{
  display:flex;gap:2px;padding:3px;border-radius:10px;
  background:rgba(30,28,35,.5);border:1px solid var(--border-l);
}
.aw-tab{
  display:flex;align-items:center;gap:5px;
  padding:8px 18px;border-radius:8px;border:none;
  background:transparent;font-size:12.5px;font-weight:600;
  color:var(--dim);cursor:pointer;font-family:inherit;
  transition:all .2s var(--ease);
}
.aw-tab:hover{color:var(--muted);}
.aw-tab-on{
  background:var(--accent);color:#f5e8ea;
}

.aw-email{font-size:11.5px;color:var(--dim);font-weight:500;}
.aw-logout{
  padding:6px 14px;border-radius:6px;border:1px solid rgba(232,69,58,.25);
  background:transparent;color:#e8736a;font-size:11.5px;font-weight:600;
  cursor:pointer;font-family:inherit;transition:all .2s var(--ease);
}
.aw-logout:hover{background:rgba(232,69,58,.08);border-color:rgba(232,69,58,.4);}

/* ── Main ── */
.aw-main{
  flex:1;overflow:auto;padding:24px 28px;
  scrollbar-width:thin;scrollbar-color:var(--border) transparent;
}
.aw-main::-webkit-scrollbar{width:5px;}
.aw-main::-webkit-scrollbar-track{background:transparent;}
.aw-main::-webkit-scrollbar-thumb{background:var(--border);border-radius:4px;}

/* ── Shared buttons ── */
.aw-btn{
  display:inline-flex;align-items:center;gap:5px;
  padding:8px 16px;border-radius:8px;font-size:12.5px;font-weight:600;
  font-family:inherit;cursor:pointer;transition:all .2s var(--ease);
  background:transparent;border:1px solid var(--border);color:var(--muted);
}
.aw-btn:hover{border-color:var(--muted);color:var(--text);}
.aw-btn:disabled{opacity:.4;cursor:not-allowed;}
.aw-btn-primary{
  display:inline-flex;align-items:center;gap:6px;
  padding:9px 20px;border-radius:8px;border:none;
  background:var(--accent);color:#f5e8ea;
  font-size:12.5px;font-weight:700;font-family:inherit;cursor:pointer;
  transition:all .2s var(--ease);
}
.aw-btn-primary:hover{background:#a02a3b;transform:translateY(-1px);}
.aw-btn-primary:disabled{opacity:.5;cursor:not-allowed;transform:none;}

/* ── Shared inputs ── */
.aw-input{padding:8px 11px;border-radius:7px;border:1px solid var(--border);background:var(--surface);color:var(--text);font-size:13px;font-family:inherit;outline:none;transition:border-color .15s var(--ease);}
.aw-input:focus{border-color:var(--accent);}
.aw-sel{
  padding:8px 11px;border-radius:7px;border:1px solid var(--border);
  background:var(--surface);color:var(--text);font-size:13px;
  font-family:inherit;outline:none;
}
.aw-label{display:block;font-size:10.5px;font-weight:700;color:var(--dim);text-transform:uppercase;letter-spacing:.6px;margin-bottom:5px;}

/* ── Shared modal ── */
.aw-modal-bg{
  position:fixed;inset:0;z-index:50;
  background:rgba(12,10,14,.6);
  display:flex;justify-content:center;align-items:center;
  animation:aw-fadein .15s ease;
}
@keyframes aw-fadein{from{opacity:0;}to{opacity:1;}}
.aw-modal{
  width:100%;max-width:520px;max-height:80vh;
  display:flex;flex-direction:column;
  background:var(--surface2);border:1px solid var(--border);border-radius:14px;
  box-shadow:0 16px 48px rgba(12,10,14,.5);overflow:hidden;
  animation:aw-modal-in .25s var(--ease);
}
@keyframes aw-modal-in{from{opacity:0;transform:translateY(-8px);}to{opacity:1;transform:none;}}
.aw-modal-hdr{display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-bottom:1px solid var(--border-l);}
.aw-modal-x{background:none;border:none;font-size:22px;color:var(--dim);cursor:pointer;line-height:1;padding:4px;transition:color .15s;}
.aw-modal-x:hover{color:var(--text);}
.aw-modal-body{flex:1;overflow:auto;padding:16px 20px;}
.aw-modal-foot{display:flex;justify-content:flex-end;gap:8px;padding:14px 20px;border-top:1px solid var(--border-l);}

/* ── Shared row actions ── */
.aw-act{
  background:none;border:none;font-size:11.5px;font-weight:600;cursor:pointer;
  font-family:inherit;margin-right:6px;transition:opacity .15s;padding:2px 4px;
}
.aw-act-orange{color:#cf4154;}.aw-act-orange:hover{text-decoration:underline;}
.aw-act-blue{color:#7db5f0;}.aw-act-blue:hover{text-decoration:underline;}
.aw-act-blue:disabled{opacity:.3;cursor:not-allowed;}
.aw-act-amber{color:#e8b040;}.aw-act-amber:hover{text-decoration:underline;}
.aw-act-red{color:#e8736a;}.aw-act-red:hover{text-decoration:underline;}

/* ── Shared animations ── */
@keyframes aw-in{0%{opacity:0;transform:translateY(8px);}100%{opacity:1;transform:translateY(0);}}
@keyframes aw-pulse{0%,100%{opacity:1;}50%{opacity:.4;}}
@keyframes aw-spin{to{transform:rotate(360deg);}}
@keyframes aw-glow{0%,100%{opacity:.7;}50%{opacity:1;}}
@keyframes aw-slidein{from{opacity:0;transform:translateX(16px);}to{opacity:1;transform:translateX(0);}}
@keyframes aw-flash{0%{opacity:1;}100%{opacity:0;}}
`;
